/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, ChangeEvent } from 'react';
import dayjs from 'dayjs';
import {
  Sizes,
  Positions,
  Icons,
  TextInput,
  Card,
} from '@sede-enterprise/shell-ds-react-framework';
import { useSelector } from 'react-redux';
import { ActionMenu, NGContainer } from './style';
import Table from 'src/UI/Table';
import Button from 'src/UI/Button';
import { searchByText } from 'src/utils/common/utils';
import { NUMBER_20 } from 'src/utils/table';
import { get } from 'src/services/GenesisApi';
import {
  DOWNLOAD_NOMINATION_GROUPS,
  GET_NOMINATION_GROUPS,
  MESSAGES,
} from 'src/constants/EUMarkets';
import DownloadButton from 'src/UI/Download';
import { sendNotification } from 'src/UI/Notification';
import { INominationGroup } from 'src/types/EUMarkets.type';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { IFullState } from 'src/types/All.type';
import { rolePermissions } from 'src/constants/Permission';
import { getEnv } from 'src/utils/env.vite';
import Parameters from '../Parameters';
import AddEditNominationGroup from './AddEdit';

const { ArrowCcw, Search, Add: AddIcon, EllipsisVertical } = Icons;

// Add/Modify are only available in the Dev environment until the save APIs are ready elsewhere.
const isDevEnvironment = () => getEnv('VITE_APP_ENVIRONMENT') === 'Dev';

const NominationGroups: React.FC = () => {
  const [nominationsGroup, setNominationsGroup] = useState<INominationGroup[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openAddEdit, setOpenAddEdit] = useState<boolean>(false);
  const [group, setGroup] = useState<INominationGroup | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const getData = () => {
    // API call to fetch data
    setLoading(true);
    get(GET_NOMINATION_GROUPS)
      .then((response) => {
        setNominationsGroup(response.data);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleModalOpenClose = () => {
    if (open) {
      setGroup(null);
    }
    setOpen(!open);
  };

  const handleAddEditOpenClose = () => {
    if (openAddEdit) {
      setGroup(null);
    }
    setOpenAddEdit(!openAddEdit);
  };

  const handleEdit = (item: INominationGroup) => {
    setGroup(item);
    setOpenAddEdit(true);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const renderData = (text: string, data: INominationGroup[]) => {
    return searchByText(data, text, [
      'nominationGroupName',
      'aliasName',
      'internalOrganizationName',
      'externalOrganizationName',
    ]);
  };

  const handleShowParams = (item: INominationGroup) => {
    setGroup(item);
    setOpen(true);
  };

  const renderActions = (item: INominationGroup) => (
    <ActionMenu
      action='click'
      popup={
        <div className='menu-container'>
          {isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData) && (
            <Button
              data-testid={`btn-modify-${item.id}`}
              size='small'
              onClick={() => handleEdit(item)}
              variant='transparent'
            >
              Modify
            </Button>
          )}
        </div>
      }
    >
      <Button
        size='small'
        data-testid={`btn-actions-${item.id}`}
        variant='transparent'
        onClick={() => {}}
        icon={<EllipsisVertical />}
        iconOnly
      />
    </ActionMenu>
  );

  const showActions = isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData);

  const columns = [
    {
      accessKey: 'nominationGroupName',
      fieldName: 'nominationGroupName',
      title: 'Nomination Group Name',
      sorting: true,
    },
    {
      accessKey: 'aliasName',
      fieldName: 'aliasName',
      title: 'Alias Name',
      sorting: true,
    },
    {
      accessKey: 'internalOrganizationName',
      fieldName: 'internalOrganizationName',
      title: 'Internal Organization Name',
      sorting: true,
    },
    {
      accessKey: 'externalOrganizationName',
      fieldName: 'externalOrganizationName',
      title: 'External Organization Name',
      sorting: true,
    },
    {
      accessKey: 'validFrom',
      fieldName: 'validFrom',
      title: 'Valid From',
      render: (item) =>
        item.validFrom
          ? dayjs(item.validFrom).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY)
          : 'No Expiry',
    },
    {
      accessKey: 'validTo',
      fieldName: 'validTo',
      title: 'Valid To',
      render: (item) =>
        item.validTo ? dayjs(item.validTo).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : 'No Expiry',
    },
    {
      accessKey: 'parameters',
      fieldName: 'parameters',
      title: 'Parameters',
      render: (item) => (
        <Button
          data-testid={`btn-params-${item.id}`}
          size='small'
          onClick={() => handleShowParams(item)}
          key={`tsk-add-btn-${item.id}`}
          variant='transparent'
          className='logs-info'
        >
          View
        </Button>
      ),
    },
    ...(showActions
      ? [
          {
            accessKey: 'actions',
            fieldName: 'actions',
            title: 'Actions',
            render: (item: INominationGroup) => renderActions(item),
          },
        ]
      : []),
  ];

  return (
    <NGContainer>
      <div className='action-container'>
        <div className='search-container'>
          <TextInput
            placeholder='Search'
            onChange={handleSearch}
            suffix={{
              node: <Search height={24} width={24} />,
            }}
            size={Sizes.Small}
          />
        </div>
        {isDevEnvironment() && permissions.includes(rolePermissions.createMasterData) && (
          <Button
            onClick={handleAddEditOpenClose}
            data-testid='add-new-nomination-group'
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
          >
            Add
          </Button>
        )}
        <DownloadButton
          downloadURL={DOWNLOAD_NOMINATION_GROUPS}
          method='GET'
          fileName='NominationGroups'
          fileType='xlsx'
          buttonProp={{
            size: Sizes.Small,
            variant: 'outlined',
            className: 'master-data-download master-data-download-secondary',
            iconOnly: true,
            'aria-label': 'Download Nomination Groups as Excel',
            title: 'Download Nomination Groups as Excel',
          }}
        />
        <Button
          size='small'
          iconOnly
          icon={<ArrowCcw />}
          onClick={getData}
          disabled={loading}
          data-testid='btn-refresh'
        />
      </div>
      <Card className='table-card'>
        <Table
          data={renderData(searchText, nominationsGroup)}
          columns={columns}
          totalRecords={renderData(searchText, nominationsGroup).length}
          recordsPerPage={NUMBER_20}
          isLoading={loading}
        />
      </Card>
      {open && (
        <Parameters
          open={open}
          onClose={handleModalOpenClose}
          params={{
            name: group?.nominationGroupName ?? '',
            parameters: group?.parameters ?? [],
          }}
          paramType='Nomination Group'
        />
      )}
      <AddEditNominationGroup
        open={openAddEdit}
        onClose={handleAddEditOpenClose}
        nominationGroup={group}
        refreshData={getData}
      />
    </NGContainer>
  );
};

export default NominationGroups;
